// Client for the SYHouse agenda proxy (/api/agenda/*). The SYW API key never
// reaches the browser — these all hit our own backend, which forwards to SYW.

export type DayStatus = 'disponivel' | 'poucas-vagas' | 'esgotado' | 'indisponivel';

export interface DisplayDay {
  date: string; // YYYY-MM-DD
  available: boolean;
  status: DayStatus;
  bookable: boolean;
  cosmetic: boolean;
}

export interface InterventionTerms {
  version?: string;
  // SYW returns the T&C per language ({pt, en, es}); `body` kept for
  // backward compat with older payloads.
  pt?: string;
  en?: string;
  es?: string;
  body?: string;
  [k: string]: unknown;
}

/** The T&C text to display — PT first, then EN/ES, then legacy `body`. */
export function termsText(t: InterventionTerms | null): string {
  return (t?.pt ?? t?.en ?? t?.es ?? t?.body ?? '').trim();
}

export interface ReserveResult {
  code: string;
  status: string;
  holdExpiresAt: string | null;
}

export interface ConfirmResult {
  code: string;
  status: string;
}

// Discriminated result so callers can branch on the real 409 gate without
// treating it as a hard error.
export type ReserveOutcome =
  | { ok: true; data: ReserveResult }
  | { ok: false; conflict: boolean; message: string };

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export async function fetchTerms(): Promise<InterventionTerms> {
  const res = await fetch('/api/agenda/terms');
  const json = (await res.json()) as ApiEnvelope<InterventionTerms>;
  if (!res.ok || !json.success || !json.data) {
    throw new Error(json.message ?? 'Não foi possível carregar os termos.');
  }
  return json.data;
}

export async function fetchAvailability(from: string, to: string): Promise<DisplayDay[]> {
  const res = await fetch(`/api/agenda/availability?from=${from}&to=${to}`);
  const json = (await res.json()) as ApiEnvelope<{ days: DisplayDay[] }>;
  if (!res.ok || !json.success || !json.data) {
    throw new Error(json.message ?? 'Não foi possível obter as disponibilidades.');
  }
  return json.data.days;
}

export async function reserveDay(input: {
  date: string;
  ref?: string;
  customer?: { name?: string; email?: string; phone?: string };
  notes?: string;
}): Promise<ReserveOutcome> {
  const res = await fetch('/api/agenda/reserve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  const json = (await res.json()) as ApiEnvelope<ReserveResult>;
  if (res.status === 409) {
    return { ok: false, conflict: true, message: json.message ?? 'Esse dia já não está disponível.' };
  }
  if (!res.ok || !json.success || !json.data) {
    return { ok: false, conflict: false, message: json.message ?? 'Não foi possível reservar o dia.' };
  }
  return { ok: true, data: json.data };
}

export async function confirmReservation(code: string): Promise<ConfirmResult> {
  const res = await fetch('/api/agenda/confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  const json = (await res.json()) as ApiEnvelope<ConfirmResult>;
  if (!res.ok || !json.success || !json.data) {
    throw new Error(json.message ?? 'Não foi possível confirmar a marcação.');
  }
  return json.data;
}
